import React, { useState } from "react";
import { ShoppingBag, Globe, Palette, Fingerprint, Megaphone, Share2, TrendingUp, Mic, Image as ImageIcon, FileText, Briefcase } from "lucide-react";

// Minimal generated cover for projects with no image (or a broken one):
// a soft tint in the field's colour, the field's icon and the project name,
// instead of the site logo. Matched on the category label shown next to it.
const FIELD_STYLES = [
    { test: /سلة|شوبيفاي|متجر|متاجر|shopify|salla/i, Icon: ShoppingBag, color: "#0d9488" },
    { test: /ووردبريس|موقع|مواقع|wordpress|web/i, Icon: Globe, color: "#2563eb" },
    { test: /هوية|شعار|لوجو|branding|logo/i, Icon: Fingerprint, color: "#7c3aed" },
    { test: /إعلان|اعلان|حملات|ads/i, Icon: Megaphone, color: "#ea580c" },
    { test: /تواصل|سوشيال|social/i, Icon: Share2, color: "#0891b2" },
    { test: /سيو|seo/i, Icon: TrendingUp, color: "#059669" },
    { test: /فويس|صوت|voice/i, Icon: Mic, color: "#dc2626" },
    { test: /بنر|بنار|banner/i, Icon: ImageIcon, color: "#ca8a04" },
    { test: /بروفايل|profile/i, Icon: FileText, color: "#475569" },
    { test: /تصميم|جرافيك|design/i, Icon: Palette, color: "#db2777" },
];

const GeneratedCover = ({ name, category }) => {
    const style = FIELD_STYLES.find((f) => f.test.test(`${category || ""} ${name || ""}`)) || { Icon: Briefcase, color: "#2563eb" };
    const { Icon, color } = style;
    return (
        <div
            role="img"
            aria-label={category ? `${name} - ${category}` : name}
            className="w-full h-full flex flex-col items-center justify-center gap-2 p-3 text-center"
            style={{ background: `linear-gradient(135deg, ${color}14 0%, ${color}29 100%)`, minHeight: "100%", aspectRatio: "4 / 3" }}
        >
            <span className="flex items-center justify-center rounded-2xl bg-white shadow-sm" style={{ width: "28%", aspectRatio: "1", maxWidth: 64, color }}>
                <Icon style={{ width: "50%", height: "50%" }} />
            </span>
            {name && (
                <span className="font-black text-gray-800 leading-tight line-clamp-2" style={{ fontSize: "clamp(10px, 3.2vw, 15px)" }}>
                    {name}
                </span>
            )}
        </div>
    );
};

// Only website/store projects get the scrolling preview. Decided by the
// category label (CATEGORY_LABELS: ووردبريس / شوبيفاي / سلة); the name is
// only a fallback when there is no category, and then only the platform
// prefixes real site projects carry ("متجر سلة X", "موقع ووردبريس X").
// A bare "متجر" can't be used: design and identity names contain it too
// ("تصاميم احترافية لمتجر X", "هوية تجارية متجر X").
const SITE_CATEGORY_TEST = /ووردبريس|شوبيفاي|سلة|موقع (ال)?[إا]لكتروني|wordpress|shopify|salla|website/i;
const SITE_NAME_TEST = /^(متجر سلة|متجر شوبيفاي|متجر ووردبريس|موقع ووردبريس|موقع إلكتروني|متجر إلكتروني|تصميم متجر إلكتروني)/;
const SITE_SCROLL_CSS = `.wz-site-scroll{object-position:50% 0%;animation:wz-site-scroll 12s ease-in-out infinite alternate}@keyframes wz-site-scroll{0%,10%{object-position:50% 0%}90%,100%{object-position:50% 100%}}`;

// Shared image renderer for every project/design thumbnail across the
// Portfolio pages. Missing or 404ing thumbnails get the generated cover
// above. Alt text = real project name + category label.
const PortfolioImage = ({ src, alt, category, className = "w-full h-full object-cover", loading = "lazy" }) => {
    const [broken, setBroken] = useState(false);
    // The backend sometimes sends the site logo as a stand-in image: treat it as "no cover".
    const placeholder = /wizfreelange\.png|logo-share/i.test(src || "");
    if (!src || broken || placeholder) return <GeneratedCover name={alt} category={category} />;
    const finalAlt = category ? `${alt} - ${category}` : alt || "معرض أعمال ويز فريلانس";
    // Website/store screenshots are full-page captures: start at the page top
    // (its hero) and slowly scroll to the bottom and back, so visitors see the
    // whole site without clicking.
    const isSite = category
        ? SITE_CATEGORY_TEST.test(category)
        : SITE_NAME_TEST.test((alt || "").trim());
    return (
        <>
            {isSite && <style>{SITE_SCROLL_CSS}</style>}
            <img
                src={src}
                alt={finalAlt}
                loading={loading}
                decoding="async"
                onError={() => setBroken(true)}
                className={isSite ? `${className} wz-site-scroll` : className}
            />
        </>
    );
};

export default PortfolioImage;
